import type { A3dRenderer } from "@axial3d/modelviewer-core";

const MAX_DIMENSION = 480;

let fbo: WebGLFramebuffer | undefined;
let fboTexture: WebGLTexture | undefined;
let pixels: Uint8Array | undefined;
let offscreen: OffscreenCanvas | undefined;
let offscreenCtx: OffscreenCanvasRenderingContext2D | undefined;
let captureWidth = 0;
let captureHeight = 0;
let lastCanvasWidth = 0;
let lastCanvasHeight = 0;
let needsCapture = false;
let latestSnapshot: Uint8Array | undefined;
let gl: WebGL2RenderingContext | undefined;

function computeCaptureSize(
  canvasWidth: number,
  canvasHeight: number,
): { width: number; height: number } {
  if (canvasWidth >= canvasHeight) {
    const scale = MAX_DIMENSION / canvasWidth;
    return {
      width: MAX_DIMENSION,
      height: Math.max(1, Math.floor(canvasHeight * scale)),
    };
  }
  const scale = MAX_DIMENSION / canvasHeight;
  return {
    width: Math.max(1, Math.floor(canvasWidth * scale)),
    height: MAX_DIMENSION,
  };
}

function createFbo(renderer: A3dRenderer, width: number, height: number): void {
  if (gl) {
    if (fbo) gl.deleteFramebuffer(fbo);
    if (fboTexture) gl.deleteTexture(fboTexture);
  }

  gl = renderer.getContext() as WebGL2RenderingContext;

  fboTexture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, fboTexture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA8,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    fboTexture,
    0,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  captureWidth = width;
  captureHeight = height;
  pixels = new Uint8Array(width * height * 4);
  offscreen = new OffscreenCanvas(width, height);
  offscreenCtx = offscreen.getContext("2d")!;
}

export function initCapture(renderer: A3dRenderer): void {
  disposeCapture();
  gl = renderer.getContext() as WebGL2RenderingContext;
}

export function markCaptureNeeded(): void {
  needsCapture = true;
}

export function captureFrame(renderer: A3dRenderer): void {
  if (!gl || !needsCapture) return;
  needsCapture = false;

  const canvasWidth = renderer.domElement.width;
  const canvasHeight = renderer.domElement.height;
  if (canvasWidth === 0 || canvasHeight === 0) return;

  if (canvasWidth !== lastCanvasWidth || canvasHeight !== lastCanvasHeight) {
    const size = computeCaptureSize(canvasWidth, canvasHeight);
    createFbo(renderer, size.width, size.height);
    lastCanvasWidth = canvasWidth;
    lastCanvasHeight = canvasHeight;
  }

  if (!fbo || !pixels || !offscreen || !offscreenCtx) return;

  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fbo);
  gl.blitFramebuffer(
    0,
    0,
    canvasWidth,
    canvasHeight,
    0,
    0,
    captureWidth,
    captureHeight,
    gl.COLOR_BUFFER_BIT,
    gl.LINEAR,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.readPixels(
    0,
    0,
    captureWidth,
    captureHeight,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    pixels,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  const w = captureWidth;
  const h = captureHeight;
  const imageData = offscreenCtx.createImageData(w, h);
  for (let row = 0; row < h; row++) {
    const srcOffset = row * w * 4;
    const dstOffset = (h - 1 - row) * w * 4;
    imageData.data.set(
      pixels.subarray(srcOffset, srcOffset + w * 4),
      dstOffset,
    );
  }
  offscreenCtx.putImageData(imageData, 0, 0);

  offscreen
    .convertToBlob({ type: "image/jpeg", quality: 0.5 })
    .then((blob) => blob.arrayBuffer())
    .then((buffer) => {
      latestSnapshot = new Uint8Array(buffer);
    });
}

export function disposeCapture(): void {
  if (gl) {
    if (fbo) gl.deleteFramebuffer(fbo);
    if (fboTexture) gl.deleteTexture(fboTexture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
  fbo = undefined;
  fboTexture = undefined;
  pixels = undefined;
  offscreen = undefined;
  offscreenCtx = undefined;
  gl = undefined;
  captureWidth = 0;
  captureHeight = 0;
  lastCanvasWidth = 0;
  lastCanvasHeight = 0;
  needsCapture = false;
  latestSnapshot = undefined;
}

export function getLatestSnapshot(): Uint8Array | undefined {
  return latestSnapshot;
}
